import { Feature, Polygon } from '@turf/turf';
import { WeatherServiceParams } from '../../types/weather';
import { getPointsInPolygon } from '../../utils/mapHelpers';
import pLimit from 'p-limit';

const MAX_CONCURRENCY = 300;  // Configurable concurrency limit
const MAX_RETRIES = 2;
const RETRY_DELAY = 500; // ms

async function fetchWithRetry(point: any, params: WeatherServiceParams, retries = 0): Promise<any> {
  try {
    const response = await fetch('https://api-dev.catxre.com/weather/weather-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: point.lat,
        lon: point.lon,
        parameter: params.parameter,
        start_date: params.startDate,
        end_date: params.endDate,
        use_proxies: true
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (retries < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchWithRetry(point, params, retries + 1);
    }
    throw error;
  }
}

export async function fetchOpenMeteoData(
  polygons: Feature<Polygon>[],
  params: WeatherServiceParams
) {
  try {
    // 1. Gather all points from all polygons into a single array
    const tasks: {
      lat: number;
      lon: number;
      polygonIndex: number;
    }[] = [];

    polygons.forEach((polygon, index) => {
      const points = getPointsInPolygon(polygon, params.resolution ?? 0.0125);
      points.forEach((pt) => {
        tasks.push({ ...pt, polygonIndex: index });
      });
    });

    if (tasks.length === 0) {
      throw new Error('No points found in the provided polygons.');
    }

    // 2. Setup progress tracking
    const totalPoints = tasks.length;
    let processedPoints = 0;

    // Initial progress update
    if (params.onProgress) {
      params.onProgress({ current: 0, total: totalPoints });
    }

    // 3. Create a concurrency-limited queue
    const limit = pLimit(MAX_CONCURRENCY);

    // 4. Map over all points and wrap each request in limit(...)
    const fetchPromises = tasks.map(pointData =>
      limit(async () => {
        try {
          const result = await fetchWithRetry(pointData, params);
          
          // Update progress
          processedPoints++;
          if (params.onProgress) {
            params.onProgress({ current: processedPoints, total: totalPoints });
          }

          return { point: pointData, result };
        } catch (err) {
          console.error(`Error processing point [${pointData.lat}, ${pointData.lon}]:`, err);
          
          // Update progress even on error
          processedPoints++;
          if (params.onProgress) {
            params.onProgress({ current: processedPoints, total: totalPoints });
          }
          
          return null;
        }
      })
    );

    // 5. Wait for all fetches to complete
    const allResults = await Promise.all(fetchPromises);

    // 6. Filter and shape the data
    const weatherData = allResults
      .filter((r): r is { point: any; result: any } => !!r && !!r.result?.daily)
      .map(({ point, result }) => ({
        lat: point.lat,
        lon: point.lon,
        times: result.daily.time,
        values: result.daily[params.parameter],
        parameter: params.parameter,
        polygonIndex: point.polygonIndex
      }));

    if (weatherData.length === 0) {
      throw new Error('No weather data found for the selected area');
    }

    return weatherData;
  } catch (error) {
    console.error('OpenMeteo service error:', error);
    throw error;
  }
}